import {createRouter, createWebHashHistory, RouteRecordRaw, createWebHistory} from 'vue-router'
import DefaultLayout from '../views/DefaultLayout.vue';
import { useActivityStore } from '../store/activityStore';
import { isLogin, checkLoginStatus } from "../utils/useAuth";

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        component: DefaultLayout,
        children: [
          {
            path: '',
            name: 'Home',
            component: () => import("../views/Handover.vue"),
            meta: { hideLayout: true }
        },
        {
            path: '/platform',
            name: 'Platform',
            component: () => import("../views/Home.vue"),
            meta: { requiresAuth: true }
          },
          {
            path: "/blog",
            name: "Blogs",
            component: () => import("../views/BlogList.vue"),
            meta: { requiresAuth: true }
        },
        {
            path: "/blog/:id",
            name: "Blog",
            component: () => import("../views/Blog.vue"),
            meta: { requiresAuth: true }
        },
          // ================ Watch/Video ================
          {
            path: "videoplayer",
            name: "VideoPlayer",
            component: () => import("../components/VideoPlayer.vue")
          },
          {
            path: "video",
            name: "Video",
            component: () => import("../views/Video.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "video/upload/:id",
            name: "Upload",
            component: () => import("../views/Upload.vue")
          },
          {
            path: "video/search/:id",
            name: "VideoSearch",
            component: () => import("../views/VideoSearch.vue"),
          },
          //================ Read ================
          {
            path: "read",
            name: "Read",
            component: () => import("../views/Read.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "ArticleSearch",
            name: "ArticleSearch",
            component: () => import("../views/ArticleSearch.vue"),
          },
          {
            path: "article/:id",
            name: "Article",
            component: () => import("../views/Article.vue")
          },
          {
            path: "free-article/:id",
            name: "FreeArticle",
            component: () => import("../views/Article.vue")
          },
          {
            path: "article-quiz/:id",
            name: "ArticleCloze",
            component: () => import("../views/ArticleCloze.vue")
          },
          //================ Speak ================
          {
            path: "speak",
            name: "Speak",
            component: () => import("../views/Speak.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "speak/list",
            name: "SpeakList",
            component: () => import("../views/SpeakList.vue")
          },
          {
            path: "speak/:id",
            name: "SpeakDetail",
            component: () => import("../views/SpeakDetail.vue")
          },
          {
            path: "create/:id",
            name: "SpeakCreate",
            component: () => import("../views/SpeakCreate.vue")
          },
          {
            path: "debate-list",
            name: "Debate",
            component: () => import("../views/Debate.vue")
          },

          //================ Mentors ================
          // Disabled — the mentor directory is not currently shipped. Commented
          // rather than deleted so it can be switched back on in one edit;
          // views/Mentors.vue is left in place.
          //
          // If you re-enable this, also restore the `router.push({ name: 'Mentors' })`
          // in views/Resume.vue (its "Back to Mentors" link), and note the route
          // below has a name collision: two routes are named "Resume" (this one
          // and /teacher/resume), so vue-router resolves the name to the other
          // one and `/mentors/resume/:id` never renders. That needs fixing
          // before the directory is useful again.
          // {
          //   path: "mentors",
          //   name: "Mentors",
          //   component: () => import("../views/Mentors.vue")
          // },
          // Disabled with the mentor directory above — this was its "View Detail"
          // target, and it has never worked anyway.
          //
          // Its name collided with the route at /teacher/resume, which is also
          // named "Resume" and registers later, so vue-router resolved the name
          // to that one and this route was left unnamed and unreachable. Nothing
          // is lost by commenting it out: `{ name: 'Resume' }` already resolved
          // to /teacher/resume everywhere.
          //
          // Consequence worth knowing, and NOT fixed by this: cms/Application.vue
          // links `{ name: 'Resume', params: { id: applicant.id } }` meaning to
          // open an applicant's profile, and instead lands on the viewer's own
          // resume with the id dropped. components/resume.vue and profile.vue
          // pass their own id, so they happen to end up where they intended.
          //
          // Restoring this route needs the name collision resolved first —
          // rename one side — or it will simply be unreachable again.
          // {
          //   path: "mentors/resume/:id",
          //   name: "Resume",
          //   component: () => import("../views/Resume.vue")
          // },
          // ================ Subscribe ================
          {
            path: "subscribe",
            name: "Subscribe",
            component: () => import("../views/Subscribe.vue")
          },
          {
            path: "order",
            name: "Order",
            component: () => import("../views/Order.vue")
          },
          {
            path: "mentor-verify-paid",
            name: "MentorVerifyPaid",
            component: () => import("../views/MentorVerifyPaid.vue")
          },
          {
            path: "cancel",
            name: "Cancel",
            component: () => import("../views/Cancel.vue")
          },
          // ================ Setting ================
          {
            path: "setting",
            name: "Setting",
            component: () => import("../views/Setting.vue")
          },
          {
            path: "payment",
            name: "Payment",
            component: () => import("../views/Payment.vue")
          },
          // ================ Information ================
          {
            path: "report",
            name: "Report",
            component: () => import("../views/Report.vue")
          },
          {
            path: "kickstart",
            name: "Kickstart",
            component: () => import("../views/Kickstart.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "kickstart-sp",
            name: "Kickstart-SP",
            component: () => import("../views/Kickstart-new.vue"),
            meta: { requiresAuth: true }
          },
          {
            path: "kickstart-en",
            name: "Kickstart-EN",
            component: () => import("../views/Kickstart-new-en.vue"),
            meta: { requiresAuth: true }
          },
          {
            path: "category/:categoryName",
            name: "Category",
            component: () => import("../views/Category.vue"),
          },
          {
            path: "level/:levelName",
            name: "Level",
            component: () => import("../views/Level.vue"),
          },
          {
            path: "founder-story",
            name: "FounderStory",
            component: () => import("../views/FounderStory.vue")
          },
          {
            path: "privacy-policy",
            name: "Privacy-Policy",
            component: () => import("../views/Policy.vue")
          },
          {
            path: "contact",
            name: "Contact",
            component: () => import("../views/Contact.vue")
          },
          {
            path: "FAQs",
            name: "FAQs",
            component: () => import("../views/FAQs.vue")
          },
          {
            path: ":catchAll(.*)",
            name: "404",
            component: () => import("../views/404.vue")
          },
        ]
      },
    // Registration
    {
        path: "/admin",
        component: () => import("../views/Admin.vue"),
        children: [
            {
                path: "register",
                name: "Register",
                component: () => import("../components/Register.vue"),
            },
            {
            path: "login",
            alias: "/user/login",
            name: "Login",
            component: () => import("../components/Login.vue"),
            },
            {
                path: "forget-password",
                name: "Forget-Pwd",
                component: () => import("../components/ForgetPwd.vue"),
            },
            {
                path: "reset-password",
                name: "Reset-Pwd",
                component: () => import("../components/ResetPwd.vue"),
            },
            {
                path: "google/auth/register/failed",
                name: "Google-register-failed",
                component: () => import("../components/VerifyingGoogleAuthRegisterFailed.vue"),
            },
            {
                path: "google/auth/login/failed",
                name: "Google-login-failed",
                component: () => import("../components/VerifyingGoogleLoginFailed.vue"),
            },
            {
                path: "email/verify",
                name: "Email-verify",
                component: () => import("../components/VerifyingEmail.vue"),
            },
            {
                path: "email-resend",
                name: "Resend Verify Email",
                component: () => import("../components/Resend.vue"),
            },
        ],
    },
    // Administration 
    {
        path: "/cms",
        redirect: '/cms/index',
        component: () => import("../views/CMS.vue"),
        children:[
            {
                path: "index",
                name: "CMS-Index",
                component: () => import("../cms/Index.vue")
            },
            {
                path: "debateList",
                name: "DebateAll",
                component: () => import("../cms/DebateAll.vue")
            },
            {
                path: "debateEdit/:id",
                name: "DebateEditWithId",
                component: () => import("../cms/DebateEditor.vue")
            },
            {
                path: "materialList",
                name: "MaterialAll",
                component: () => import("../cms/MaterialAll.vue")
            },
            {
                path: "materialCreate",
                name: "MaterialCreate",
                component: () => import("../cms/MaterialEditor.vue")
            },
            {
                path: "materialEdit/:id",
                name: "MaterialEditWithId",
                component: () => import("../cms/MaterialEditor.vue")
            },
            {
                path: "userActivity",
                name: "UserAtivity",
                component: () => import("../cms/UserActivity.vue")
            },
            {
                path: "userlist",
                name: "UserList",
                component: () => import("../cms/UserList.vue")
            },
            {
                path: "mentorlist",
                name: "MentorList",
                component: () => import("../cms/MentorList.vue")
            },
            {
                path: "orglist",
                name: "OrgList",
                component: () => import("../cms/OrgList.vue")
            },
            {
                path: "orderlist",
                name: "OrderList",
                component: () => import("../cms/OrderList.vue")
            },
            {
                path: "articleAudioList",
                name: "ArticleAudioList",
                component: () => import("../cms/ArticleAudio.vue")
            },
            {
                path: "articleList",
                name: "ArticleList",
                component: () => import("../cms/ArticleList.vue")
            },
            {
                path: "articleEdit/:id",
                name: "ArticleEditWithId",
                component: () => import("../cms/ArticleEditor.vue")
            },
            {
                path: "articleEdit",
                name: "ArticleEdit",
                component: () => import("../cms/ArticleEditor.vue")
            },
            {
                path: "blogPostList",
                name: "BlogPostList",
                component: () => import("../cms/BlogPostList.vue")
            },
            {
                path: "blogPostEdit/:id",
                name: "BlogPostEditWithId",
                component: () => import("../cms/BlogPostEditor.vue")
            },
            {
                path: "blogPostEdit",
                name: "BlogPostEdit",
                component: () => import("../cms/BlogPostEditor.vue")
            },
            {
                path: "magazineList",
                name: "MagazineList",
                component: () => import("../cms/MagazineList.vue")
            },
            {
                path: "magazineEdit",
                name: "MagazineEdit",
                component: () => import("../cms/MagazineEditor.vue")
            },
            {
                path: "magazineEdit/:id",
                name: "MagazineEditWithId",
                component: () => import("../cms/MagazineEditor.vue")
            },
            {
                path: "eventList",
                name: "EventList",
                component: () => import("../cms/EventList.vue")
            },
            {
                path: "eventEdit/:id",
                name: "EventEditWithId",
                component: () => import("../cms/EventEditor.vue")
            },
            {
                path: "eventEdit",
                name: "EventEdit",
                component: () => import("../cms/EventEditor.vue")
            },
            {
                path: "categoryList",
                name: "CategoryList",
                component: () => import("../cms/CategoryList.vue")
            },
            {
                path: "cartoonList",
                name: "CartoonList",
                component: () => import("../cms/CartoonList.vue")
            },
            {
                path: "cartoonEditor",
                name: "CartoonEditor",
                component: () => import("../cms/CartoonEditor.vue")
            },
            {
                path: "adList",
                name: "AdList",
                component: () => import("../cms/AdList.vue")
            },
            {
                path: "adEditor",
                name: "AdEditor",
                component: () => import("../cms/AdEditor.vue")
            },
            {
                path: "application",
                name: "Application",
                component: () => import("../cms/Application.vue")
            },
            {
                path: "videoCategory",
                name: "videoCategory",
                component: () => import("../cms/VideoCategory.vue")
            },
            {
                path: "VideoUpload",
                name: "VideoUpload",
                component: () => import("../cms/VideoUpload.vue")
            },
            {
                path: "VideoEdit/:id",
                name: "VideoEditor",
                component: () => import("../cms/VideoEditor.vue")
            },
            {
                path: "VideoTranscript/:id",
                name: "VideoTranscript",
                component: () => import("../cms/VideoTranscript.vue")
            },
            {
                path: "CategoryEdit",
                name: "EditCategory",
                component: () => import("../cms/EditCategory.vue")
            }
        ]
    },
    {
        path: "/user",
        redirect: '/user/center',
        component: () => import("../views/newDashboard.vue"),
        children: [
            {
                path: "center",
                name: "Center",
                component: () => import("../components/report.vue"),
            },
            // {
            //     path: "organization",
            //     name: "Organization",
            //     component: () => import("../components/Dashboard/organization.vue"),
            // },
            // {
            //     path: "group",
            //     name: "Group",
            //     component: () => import("../components/Dashboard/group.vue"),
            // },
            // {
            //     path: "member",
            //     name: "Member",
            //     component: () => import("../components/Dashboard/member.vue"),
            // },
            // {
            //     path: "member/recording/:id",
            //     name: "MemberRecording",
            //     component: () => import("../components/Dashboard/memberRecording.vue")
            // },
            // {
            //     path: "profile",
            //     name: "Profile",
            //     component: () => import("../components/profile.vue"),
            // },
            // {
            //     path: "account",
            //     name: "Account",
            //     component: () => import("../components/account.vue"),
            // },
            {
                path: "notification",
                name: "Motification",
                component: () => import("../components/notificationList.vue"),
            },
            {
                path: "quiz",
                name: "Quiz",
                component: () => import("../components/quiz.vue"),
            },
            {
                path: "quiz/:queryId/:quizId",
                name: "QuizDetail",
                component: () => import("../components/quizDetail.vue"),
            },
            {
                path: "record",
                name: "Record",
                component: () => import("../components/recordingList.vue"),
            },
            {
                path: "discussion",
                name: "Discussion",
                component: () => import("../components/discussion.vue"),
            },
            {
                path: "activities",
                name: "Activities",
                component: () => import("../components/activities.vue"),
            },
            {
                path: "event/:id",
                name: "Event",
                component: () => import("../components/Event.vue"),
            },
            {
                path: "uploads",
                name: "Uploads",
                component: () => import("../components/uploads.vue"),
            },
            {
                path: "download",
                name: "Download",
                component: () => import("../components/download.vue"),
            },
            {
                path: "popularity",
                name: "Popularity",
                component: () => import("../components/popularity.vue"),
            },
       

            {
                path: "subscription",
                name: "Subscription",
                component: () => import("../components/subscription.vue"),
            },
            {
                path: "archived",
                name: "Archived",
                component: () => import("../components/archived.vue"),
            },
            {
                path: "vocabulary",
                redirect: '/user/vocabulary/notebook',
                children: [
                    {
                        path: "flipcard",
                        name: "Flipcard",
                        component: () => import("../components/flipcard.vue"),
                    },
                    {
                        path: "lookup",
                        name: "Lookup",
                        component: () => import("../components/lookup.vue"),
                    },
                    {
                        path: "notebook",
                        name: "Notebook",
                        component: () => import("../components/notebook.vue"),
                    },
     
                ]
            },
            {
                path: "watch",
                redirect: '/user/watch',
                children: [

                    {
                        path: "sentencelist",
                        name: "sentencelist",
                        component: () => import("../components/sentencelist.vue"),
                    },
                    {
                        path: "videolist",
                        name: "videolist",
                        component: () => import("../components/videolist.vue"),
                    },
                ]
            },

        ],
    },
    {
        path: "/teacher",
        redirect: '/teacher/organization/list',
        component: () => import("../views/TeacherCenter.vue"),
        children: [
      
            {
                path: "organization",
                children: [
                    {
                        path: "list",
                        name: "Organization",
                        component: () => import("../components/Dashboard/organization.vue"),
                    },
                    {
                        path: "group",
                        name: "Group",
                        component: () => import("../components/Dashboard/group.vue"),
                    },
                    {
                        path: "member",
                        name: "Member",
                        component: () => import("../components/Dashboard/member.vue"),
                    },
                    {
                        path: "member/recording/:id",
                        name: "MemberRecording",
                        component: () => import("../components/Dashboard/memberRecording.vue")
                    },
     
                ]
            },
            {
                path: "profile",
                name: "Profile",
                component: () => import("../components/profile.vue"),
            },
            {
                path: "resume",
                name: "Resume",
                component: () => import("../components/resume.vue"),
            },

        ],
    },



    {
        path: "/debate/room",
        name: "DebateRoom",
        component: () => import("../views/DebateRoom.vue")
    },
    {
        path: "/debate/room/:id",
        name: "DebateRoom-participant",
        component: () => import("../views/speakRoom.vue")
    },
    {
        path: "/minitalk/room/:id",
        name: "Mini-Talk",
        component: () => import("../views/MiniTalkRoom.vue")
    },
    {
        path: "/roleplay/room/:id",
        name: "Role-Play",
        component: () => import("../views/RolePlayRoom.vue")
    },
    {
        path: "/speak-room/:id",
        name: "speak",
        component: () => import("../views/speakRoom.vue")
      },

]


const router = createRouter({

    history: createWebHistory(),
    routes,
    scrollBehavior (to, from, savedPosition) {
        // return { top: 0 }
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }

    },
    
    linkActiveClass: 'active'

})

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const activityStore = useActivityStore();

if (requiresAuth) {
  await checkLoginStatus();

  if (!isLogin.value && !localStorage.getItem("token")) {
    next({
      path: "/admin/login",
      query: { redirect: to.fullPath },
    });
    return;
  }
}

  if (from.name) {
    activityStore.logPageExit(from.name as string);
  }

  if (to.name) {
    activityStore.logPageVisit(to.name as string);
  }

  next();
});
export default router